import {NTX} from "./texts";

/** Событие, о котором решаем, показывать ли системное уведомление. */
export type AttentionEvent =
    /** Алерт поднят (`notification` с `type: 'alert'`); actorId — кто поднял. */
    | {type: "alert"; actorId: string | null}
    /** Сообщение чата (`chat:message`): канал — uuid машины или `general`. */
    | {type: "message"; authorId: string | null; kind: string; channel: string};

export interface AttentionContext {
    me: string | null;
    /** Вкладка видна и окно в фокусе — пользователь смотрит на приложение. */
    pageActive: boolean;
    /** Переключатель приложения (S4.3): false — без системных уведомлений и звуков. Не задан — включено. */
    enabled?: boolean;
}

/**
 * Показывать ли системное (браузерное) уведомление (S4, часть Б):
 * - уведомления выключены переключателем приложения — никогда (S4.3);
 * - своё действие — никогда;
 * - пользователь смотрит на вкладку — никогда (хватает всплывающего и счётчика);
 * - алерт — да;
 * - сообщение пользователя в общем чате или в треде любой машины — да (S9);
 * - системные строки чата — нет (только счётчик / отдельный алерт).
 */
export const shouldShowSystemNotification = (event: AttentionEvent, ctx: AttentionContext): boolean => {
    if (ctx.enabled === false) return false;
    if (ctx.pageActive) return false;
    if (event.type === "alert") return !ctx.me || event.actorId !== ctx.me;
    if (event.kind !== "user" || !event.authorId) return false;
    return !ctx.me || event.authorId !== ctx.me;
};

/** Звук алерта: всем, кроме поднявшего, независимо от видимости вкладки; выключено переключателем — нет (S4.3). */
export const shouldBeepAlert = (actorId: string | null, me: string | null, enabled = true): boolean =>
    enabled && (!me || actorId !== me);

/**
 * Что делать, когда машина пришла на наш этап (S4.2, S4.3): всплывающее внутри
 * приложения — всегда; системное уведомление и тихий сигнал — если вкладка не
 * на экране и уведомления не выключены переключателем.
 */
export const stageArrivalSignals = (
    pageActive: boolean,
    enabled: boolean,
): {toast: boolean; system: boolean; sound: boolean} => ({
    toast: true,
    system: !pageActive && enabled,
    sound: !pageActive && enabled,
});

/** Заголовок вкладки: `({N}) ATOM EoL`, N — сумма непрочитанного по всем каналам; 0 — без скобок. */
export const tabTitle = (unread: Record<string, number>): string => {
    const n = Object.values(unread).reduce((sum, v) => sum + (v > 0 ? v : 0), 0);
    return n > 0 ? `(${n}) ${NTX.brand}` : NTX.brand;
};

/** Строка журнала `moved` (`board:event`) — то, что нужно правилу «машина пришла на наш этап». */
export interface StageArrival {
    actorId: string | null;
    fromStage: string | null;
    toStage: string | null;
}

/**
 * Машина пришла на наш этап (S4.2): перенос в колонку с `takeable` — да;
 * своё действие, колонка без `takeable` или та же колонка — нет.
 */
export const shouldNotifyStageArrival = (
    e: StageArrival,
    stages: ReadonlyArray<{key: string; takeable: boolean}>,
    me: string | null,
): boolean => {
    if (!e.toStage || e.toStage === e.fromStage) return false;
    if (me && e.actorId === me) return false;
    return !!stages.find((s) => s.key === e.toStage)?.takeable;
};

/** Карточка с алертом, который никто не взял (только нужные поля). */
interface AlertCar {
    vin: string;
    alert: {taken_by: unknown; raised_at: string | null; raised_by?: {id: string} | null} | null;
}

/**
 * VIN машины с самым свежим алертом, который никто не взял; нет такого — null.
 * Алерт, который поднял я (`me`), не в счёт: своё действие не мигает, как и не звучит (S4.3).
 */
export const untakenAlertVin = (cars: Iterable<AlertCar>, me: string | null = null): string | null => {
    let best: AlertCar | null = null;
    for (const c of Array.from(cars)) {
        if (!c.alert || c.alert.taken_by) continue;
        if (me && c.alert.raised_by?.id === me) continue;
        if (!best || (c.alert.raised_at ?? "") > (best.alert?.raised_at ?? "")) best = c;
    }
    return best ? best.vin : null;
};

/**
 * Заголовок вкладки с миганием (S4.2): есть незабранный алерт и вкладка не на
 * экране — нечётный такт «Алерт · {хвост VIN}», чётный обычный. Иначе обычный.
 */
export const blinkingTitle = (base: string, alertTail: string | null, pageActive: boolean, tick: number): string =>
    alertTail && !pageActive && tick % 2 === 1 ? NTX.alertBlink(alertTail) : base;

/** Текст уведомления о сообщении: начало текста одной строкой или «Вложение». */
export const messagePreview = (text: string, hasAttachments: boolean, max = 120): string => {
    const line = text.replace(/\s+/g, " ").trim();
    if (!line) return hasAttachments ? NTX.attachment : "";
    return line.length > max ? `${line.slice(0, max - 1)}…` : line;
};

/** `tag` уведомления: одинаковые не плодятся — одно на машину (алерт) и на канал (сообщения). */
export const notificationTag = (
    event: {type: "alert" | "stage" | "milestone"; carId: string} | {type: "message"; channel: string},
): string => (event.type === "message" ? `chat:${event.channel}` : `${event.type}:${event.carId}`);
